import { createVeiculo, getModelos, getVeiculoById, updateVeiculo } from '@spark/services/frotas';
import React, { useMemo, useCallback, useState, useEffect } from 'react';
import { Form } from 'react-final-form';
import { useHistory, useParams } from 'react-router-dom';
import { Button, ButtonWithLoading } from '@spark/ui-components/components/Button';
import { Card, CardBody } from '@spark/ui-components/components/Card';
import { FormButtonToolbar, FormContainer } from '@spark/ui-components/components/form/FormElements';
import DynamicForm from '@spark/ui-components/components/form/DynamicForm';
import { notification } from '@spark/ui-components/utils/notification';
import { updateMultipleFieldsByName } from '@spark/ui-components/utils/formHelpers';
import { buildFormSections, buildFormStructure, validateForm } from './formConfig';

const toStringOrEmpty = (value) => (value === undefined || value === null ? '' : String(value));

const mapApiToForm = (veiculo = {}) => {
  return {
    id: veiculo.id ?? veiculo.Id ?? '',
    frota: toStringOrEmpty(veiculo.frota ?? veiculo.Frota),
    placa: toStringOrEmpty(veiculo.placa ?? veiculo.Placa),
    renavam: toStringOrEmpty(veiculo.renavam ?? veiculo.Renavam),
    uf: (() => {
      const ufValue = toStringOrEmpty(veiculo.uf ?? veiculo.Uf ?? veiculo.UF);
      return ufValue ? ufValue.toUpperCase() : '';
    })(),
    idModeloVeicular: veiculoModeloParaSelect(veiculo),
  };
};

const mapModelosParaOptions = (registros = []) =>
  registros
    .map((modelo) => {
      const idModelo = modelo.id ?? modelo.Id;
      if (!idModelo) return null;
      const descricao =
        modelo.descricao ??
        modelo.Descricao ??
        modelo.nome ??
        modelo.Nome ??
        `Modelo ${idModelo}`;
      return {
        value: String(idModelo),
        label: toStringOrEmpty(descricao) || `Modelo ${idModelo}`,
      };
    })
    .filter(Boolean)
    .sort((a, b) => a.label.localeCompare(b.label, 'pt-BR'));

const veiculoModeloParaSelect = (veiculo = {}) => {
  const modelo = veiculo.modeloVeicular ?? veiculo.ModeloVeicular ?? {};
  const idModelo =
    veiculo.idModeloVeicular ??
    veiculo.IdModeloVeicular ??
    modelo.id ??
    modelo.Id ??
    null;
  if (!idModelo) return null;

  const descricao =
    modelo.descricao ??
    modelo.Descricao ??
    modelo.nome ??
    modelo.Nome ??
    `Modelo ${idModelo}`;

  return {
    value: String(idModelo),
    label: toStringOrEmpty(descricao) || `Modelo ${idModelo}`,
  };
};

const sanitizeText = (value) => (typeof value === 'string' ? value.trim() : '');
const sanitizePlaca = (value) => sanitizeText(value).replace(/\s+/g, '').toUpperCase();
const sanitizeRenavam = (value) => sanitizeText(value).replace(/\D+/g, '');
const sanitizeUF = (value) => sanitizeText(value).slice(0, 2).toUpperCase();
const toNumberOrNull = (value) => {
  if (value === undefined || value === null || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const mapFormToApi = (values) => {
  const modeloSelecionado = values.idModeloVeicular;
  const modeloValue =
    typeof modeloSelecionado === 'object' && modeloSelecionado !== null
      ? modeloSelecionado.value
      : modeloSelecionado;

  return {
    frota: sanitizeText(values.frota),
    placa: sanitizePlaca(values.placa),
    renavam: (() => {
      const renavam = sanitizeRenavam(values.renavam);
      return renavam ? renavam : null;
    })(),
    uf: (() => {
      const uf = sanitizeUF(values.uf);
      return uf ? uf : null;
    })(),
    idModeloVeicular: toNumberOrNull(modeloValue),
  };
};

const Forms = ({ isReadOnly = false }) => {
  const history = useHistory();
  const { id } = useParams();

  const [loading, setLoading] = useState(false);
  const [initialData, setInitialData] = useState(null);
  const [loadingData, setLoadingData] = useState(false);
  const [modeloOptions, setModeloOptions] = useState([]);
  const [loadingModelos, setLoadingModelos] = useState(false);

  const isEditMode = !!id && !isReadOnly;

  const defaultValues = useMemo(
    () => ({
      id: '',
      frota: '',
      placa: '',
      renavam: '',
      uf: '',
      idModeloVeicular: null,
    }),
    [],
  );

  useEffect(() => {
    let ativo = true;

    const carregarModelos = async () => {
      try {
        setLoadingModelos(true);
        const response = await getModelos({ limit: 25 });
        if (!ativo) return;

        const registros = response?.registros ?? response?.Registros ?? [];
        const options = mapModelosParaOptions(registros);
        setModeloOptions(options);
      } catch (error) {
        if (ativo) {
          setModeloOptions([]);
        }
        console.error('Erro ao carregar modelos de veiculos:', error);
        notification.error('Nao foi possivel carregar a lista de modelos de veiculos.');
      } finally {
        if (ativo) {
          setLoadingModelos(false);
        }
      }
    };

    carregarModelos();
    return () => {
      ativo = false;
    };
  }, []);

  useEffect(() => {
    let ativo = true;

    if ((isReadOnly || isEditMode) && id) {
      const carregarDados = async () => {
        try {
          setLoadingData(true);
          const resposta = await getVeiculoById(id);
          if (!ativo) return;

          const veiculo = resposta?.veiculo ?? resposta?.Veiculo ?? resposta ?? {};
          const valoresInicial = { ...defaultValues, ...mapApiToForm(veiculo) };
          setInitialData(valoresInicial);

          const optionAtual = valoresInicial.idModeloVeicular;
          if (optionAtual) {
            setModeloOptions((prev) => {
              const jaExiste = prev.some((opt) => opt.value === optionAtual.value);
              if (jaExiste) return prev;
              return [optionAtual, ...prev];
            });
          }
        } catch (error) {
          console.error('Erro ao carregar veiculo:', error);
          notification.error('Nao foi possivel carregar os dados do veiculo.');
        } finally {
          if (ativo) {
            setLoadingData(false);
          }
        }
      };

      carregarDados();
      return () => {
        ativo = false;
      };
    }

    setInitialData(defaultValues);
    return undefined;
  }, [defaultValues, id, isEditMode, isReadOnly]);

  const onSubmit = useCallback(
    async (values) => {
      try {
        setLoading(true);
        const payload = mapFormToApi(values);

        if (payload.idModeloVeicular === null) {
          notification.error('Selecione um modelo de veiculo.');
          return;
        }

        if (isEditMode) {
          await updateVeiculo(id, payload);
          notification.success('Veiculo atualizado com sucesso!');
        } else {
          await createVeiculo(payload);
          notification.success('Veiculo criado com sucesso!');
        }

        history.push('/pages/veiculos');
      } catch (error) {
        console.error('Erro ao salvar veiculo:', error);
        notification.error('Nao foi possivel salvar o veiculo.');
      } finally {
        setLoading(false);
      }
    },
    [history, id, isEditMode],
  );

  const currentInitialValues = initialData || defaultValues;
  const handleSearchModelos = useCallback(
    async (termo = '') => {
      try {
        const response = await getModelos({
          search: termo,
          limit: 20,
        });
        const registros = response?.registros ?? response?.Registros ?? [];
        const options = mapModelosParaOptions(registros);

        if (options.length > 0) {
          setModeloOptions((prev) => {
            const existentes = new Map(prev.map((opt) => [opt.value, opt]));
            options.forEach((opt) => existentes.set(opt.value, opt));
            return Array.from(existentes.values());
          });
        }

        return options;
      } catch (error) {
        console.error('Erro ao buscar modelos de veiculos:', error);
        return [];
      }
    },
    [],
  );

  const structure = useMemo(() => {
    const baseStructure = buildFormStructure(modeloOptions, { loadingModelos });
    return updateMultipleFieldsByName(baseStructure, {
      idModeloVeicular: {
        onSearch: handleSearchModelos,
      },
    });
  }, [modeloOptions, loadingModelos, handleSearchModelos]);
  const sections = useMemo(() => buildFormSections(currentInitialValues), [currentInitialValues]);

  if ((isReadOnly || isEditMode) && loadingData) {
    return (
      <Card>
        <CardBody>
          <div style={{ textAlign: 'center', padding: '50px' }}>Carregando dados do veiculo...</div>
        </CardBody>
      </Card>
    );
  }

  if (loadingModelos && !modeloOptions.length) {
    return (
      <Card>
        <CardBody>
          <div style={{ textAlign: 'center', padding: '50px' }}>Carregando modelos de veiculos...</div>
        </CardBody>
      </Card>
    );
  }

  return (
    <Form
      key={id || 'novo'}
      onSubmit={onSubmit}
      initialValues={currentInitialValues}
      validate={validateForm}
      keepDirtyOnReinitialize={false}
    >
      {({ handleSubmit }) => (
        <Card>
          <CardBody>
            <FormContainer onSubmit={handleSubmit}>
              <DynamicForm
                structure={structure}
                sections={sections}
                isViewMode={isReadOnly}
                isEditing={isEditMode}
              />

              {!isReadOnly && (
                <FormButtonToolbar className="mt-5">
                  <ButtonWithLoading variant="primary" type="submit" loading={loading || undefined}>
                    {isEditMode ? 'Atualizar' : 'Salvar'}
                  </ButtonWithLoading>
                  <Button variant="secondary" type="button" onClick={() => history.goBack()}>
                    Cancelar
                  </Button>
                </FormButtonToolbar>
              )}
            </FormContainer>
          </CardBody>
        </Card>
      )}
    </Form>
  );
};

export default Forms;
